import { Controller } from "@hotwired/stimulus";
import { countryOptions, defaultCountry, format, fromE164, toE164 } from "../phone.js";

// Progressively enhances a phone input into a country picker plus as-you-type
// formatting, writing strict E.164 into a hidden field and blocking submit on an
// invalid number. The server re-validates with Phonelib regardless. The `value`
// value seeds an existing number, and `optional` lets an empty field submit so a
// number can be left out or a stored one cleared. A submitter marked
// formnovalidate always goes through, because the server discards the number on
// a skip whatever was typed.
export default class extends Controller {
  static targets = ["country", "input", "e164", "error"];
  static values = { value: String, optional: Boolean };

  connect() {
    this.populateCountries();
    const seeded = this.valueValue ? fromE164(this.valueValue) : null;
    this.countryTarget.value = seeded?.country || defaultCountry(this.region());
    if (seeded?.national) {
      // Trust the server-validated number as it was stored. Recomputing it through
      // toE164 would blank the hidden field whenever the pinned client library rates
      // a stored number invalid, which then blocks even a name-only save.
      this.inputTarget.value = format(seeded.national, this.countryTarget.value);
      this.e164Target.value = this.valueValue;
    } else {
      this.reformat();
    }
    this.onSubmit = (event) => this.validate(event);
    this.element.closest("form")?.addEventListener("submit", this.onSubmit);
  }

  disconnect() {
    this.element.closest("form")?.removeEventListener("submit", this.onSubmit);
  }

  countryChanged() {
    this.reformat();
    this.clearError();
  }

  inputChanged() {
    this.reformat();
    this.clearError();
  }

  validate(event) {
    if (event.submitter?.hasAttribute("formnovalidate")) return;
    if (this.e164Target.value) return;
    if (this.optionalValue && !this.inputTarget.value.trim()) return;

    event.preventDefault();
    this.errorTarget.textContent = "Enter a valid phone number for the selected country.";
    this.errorTarget.hidden = false;
  }

  reformat() {
    const country = this.countryTarget.value;
    this.inputTarget.value = format(this.inputTarget.value, country);
    this.e164Target.value = toE164(this.inputTarget.value, country) || "";
  }

  clearError() {
    this.errorTarget.hidden = true;
  }

  populateCountries() {
    this.countryTarget.replaceChildren(
      ...countryOptions(navigator.language).map((option) => {
        const element = document.createElement("option");
        element.value = option.code;
        element.textContent = `${option.flag} ${option.name} +${option.callingCode}`;
        return element;
      })
    );
  }

  region() {
    try {
      return new Intl.Locale(navigator.language).region || "";
    } catch {
      return "";
    }
  }
}
